<script setup>
/**
 * One menu, written or rewritten. `TaskEditorPage`'s shape, line for line: one component for
 * `waslo_menu_new` and `waslo_menu_edit`, a create that `router.replace`s onto the edit route,
 * the docked five-state `WasloSaveBar`, validation before the write so a failure is the
 * connection and not something she typed, and removal as two clicks in place.
 *
 * TWO DIFFERENCES, each forced by the data:
 *   • The TASK PICKER's read degrades in place, like a task's connections: without it the form
 *     still opens and every option keeps the task it already has.
 *   • One rule only the server can check — "another menu already has this name" — comes back
 *     as `validation_failed` with the field named, so a failed save pins `error.fields` under
 *     the box it belongs to instead of only saying "could not save".
 */
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import MenuForm from '../components/MenuForm.vue';
import RestoreHistory from '../components/RestoreHistory.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import WasloSaveBar from '../components/WasloSaveBar.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useCustomerLanguages } from '../composables/useCustomerLanguages.js';
import { toIntlLocale } from '../helpers/intl.js';
import {
  blankDraft,
  draftToWire,
  menuPatch,
  toDraft,
  validateDraft,
} from '../helpers/menu-draft.js';
import { WASLO_ERROR_CODES, wasloApi } from '../api/client.js';

const { t, locale } = useWasloI18n();
const route = useRoute();
const router = useRouter();
const accountId = useWasloAccountId();
/** P-7.1 and P-2.10: which language a row needs follows "Your customers write in". */
const languages = useCustomerLanguages(accountId);

const positiveInt = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const menuId = ref(positiveInt(route?.params?.menuId));
const isNew = computed(() => menuId.value === null);

const draft = ref(blankDraft());
const savedWire = ref(draftToWire(blankDraft()));
const fieldErrors = ref({});
const isSaving = ref(false);
const saveFailed = ref(false);
const savedAt = ref(null);
const savedSections = ref('');
const isRemoving = ref(false);
const historyRef = ref(null);
const confirmingRemoval = ref(false);

const menu = useWasloApi(
  signal => wasloApi.getMenu(accountId.value, menuId.value, signal),
  { immediate: false }
);

const tasks = useWasloApi(
  signal => wasloApi.getMenuTasks(accountId.value, signal),
  { isEmpty: () => false }
);

const adopt = value => {
  draft.value = toDraft(value);
  savedWire.value = draftToWire(draft.value);
  fieldErrors.value = {};
};

/** Whether there is a form to show yet — see TaskEditorPage's `ready` for why it is ours. */
const ready = ref(false);

const loadMenu = async () => {
  ready.value = false;
  await menu.reload();
  if (menu.data.value) adopt(menu.data.value);
  ready.value = true;
};

onMounted(() => {
  if (isNew.value) {
    ready.value = true;
    return;
  }
  loadMenu();
});

const isFetching = computed(() => !ready.value || tasks.isFetching.value);

const errorMessage = computed(() => {
  const error = menu.error.value;
  if (!error) return '';
  return error.code === WASLO_ERROR_CODES.NOT_FOUND
    ? t('WASLO.MENUS.EDITOR.NOT_FOUND')
    : t('WASLO.COMMON.ERROR_BODY');
});

const headerTitle = computed(() => {
  if (isNew.value) return t('WASLO.MENUS.EDITOR.NEW_TITLE');
  return draft.value.title.trim() || t('WASLO.MENUS.TITLE');
});

/**
 * Back to the list — which is now the MENUS TAB of Tasks, addressed directly.
 *
 * `…/waslo/menus` still resolves and would land in the same place, because that record is
 * kept as a redirect precisely so old bookmarks keep working. But "back" on a screen the
 * owner is standing on is not an old bookmark, and a Back button that navigates to a URL in
 * order to be sent somewhere else puts an extra entry in her history and a flicker on her
 * screen for nothing. The redirect is for links we do not control; this one we do.
 */
const listUrl = computed(() =>
  accountId.value === null
    ? null
    : `${frontendURL(`accounts/${accountId.value}/waslo/tasks`)}?tab=menus`
);

const taskChoices = computed(() => tasks.data.value ?? null);

const patch = computed(() => menuPatch(draft.value, savedWire.value));
const isDirty = computed(() => Object.keys(patch.value).length > 0);

const SECTION_LABEL_KEY = {
  title: 'WASLO.MENUS.EDITOR.ABOUT.HEADING',
  whenToShow: 'WASLO.MENUS.EDITOR.ABOUT.HEADING',
  options: 'WASLO.MENUS.EDITOR.OPTIONS.HEADING',
  bodyEn: 'WASLO.MENUS.EDITOR.MESSAGE.HEADING',
  bodyAr: 'WASLO.MENUS.EDITOR.MESSAGE.HEADING',
  buttonEn: 'WASLO.MENUS.EDITOR.MESSAGE.HEADING',
  buttonAr: 'WASLO.MENUS.EDITOR.MESSAGE.HEADING',
  enabled: 'WASLO.MENUS.EDITOR.STATE.HEADING',
};

const nameSections = keys => {
  const names = [];
  keys.forEach(key => {
    const label = SECTION_LABEL_KEY[key];
    if (label && !names.includes(label)) names.push(label);
  });
  return names.map(label => t(label)).join(t('WASLO.COMMON.LIST_SEPARATOR'));
};

const pendingSections = computed(() => nameSections(Object.keys(patch.value)));

const savedTime = computed(() =>
  savedAt.value
    ? new Intl.DateTimeFormat(toIntlLocale(locale.value), {
        hour: 'numeric',
        minute: '2-digit',
      }).format(savedAt.value)
    : ''
);

const saveState = computed(() => {
  if (isSaving.value) return 'saving';
  if (saveFailed.value) return 'error';
  if (isNew.value || isDirty.value) return 'dirty';
  if (savedAt.value) return 'saved';
  return 'idle';
});

const saveStatus = computed(() => {
  if (isSaving.value) return t('WASLO.MENUS.EDITOR.STATUS.SAVING');
  if (saveFailed.value) return t('WASLO.MENUS.EDITOR.STATUS.ERROR');
  if (isNew.value) return t('WASLO.MENUS.EDITOR.STATUS.NEW');
  if (isDirty.value) return t('WASLO.MENUS.EDITOR.STATUS.DIRTY');
  if (savedAt.value) {
    return t('WASLO.MENUS.EDITOR.STATUS.SAVED', { time: savedTime.value });
  }
  return t('WASLO.MENUS.EDITOR.STATUS.IDLE');
});

const saveDetail = computed(() => {
  if (isSaving.value) return '';
  if (saveFailed.value) return t('WASLO.MENUS.EDITOR.STATUS.ERROR_DETAIL');
  if (isNew.value) return t('WASLO.MENUS.EDITOR.STATUS.NEW_DETAIL');
  if (isDirty.value) {
    return t('WASLO.MENUS.EDITOR.STATUS.DIRTY_DETAIL', {
      sections: pendingSections.value,
    });
  }
  if (savedAt.value) {
    return t('WASLO.MENUS.EDITOR.STATUS.SAVED_DETAIL', {
      sections: savedSections.value,
    });
  }
  return t('WASLO.MENUS.EDITOR.STATUS.IDLE_DETAIL');
});

const saveLabel = computed(() =>
  isNew.value ? t('WASLO.MENUS.EDITOR.CREATE') : t('WASLO.MENUS.EDITOR.SAVE')
);

const onDraftChange = value => {
  draft.value = value;
  if (saveFailed.value) saveFailed.value = false;
};

const save = async () => {
  if (isSaving.value) return;
  if (!isNew.value && !isDirty.value) return;

  const errors = validateDraft(draft.value, t, languages.value);
  fieldErrors.value = errors;
  if (Object.keys(errors).length > 0) return;

  isSaving.value = true;
  saveFailed.value = false;
  const carried = Object.keys(patch.value);
  try {
    if (isNew.value) {
      const created = await wasloApi.createMenu(
        accountId.value,
        draftToWire(draft.value)
      );
      adopt(created);
      menuId.value = created.id;
      savedSections.value = '';
      savedAt.value = new Date();
      useAlert(t('WASLO.MENUS.EDITOR.CREATED'));
      if (accountId.value !== null) {
        await router.replace(
          frontendURL(`accounts/${accountId.value}/waslo/menus/${created.id}`)
        );
      }
    } else {
      const saved = await wasloApi.updateMenu(
        accountId.value,
        menuId.value,
        patch.value
      );
      adopt(saved);
      savedSections.value = nameSections(carried);
      savedAt.value = new Date();
      useAlert(t('WASLO.MENUS.EDITOR.SAVED'));
      historyRef.value?.reload();
    }
  } catch (error) {
    saveFailed.value = true;
    // `validation_failed` carries the fields by request-body key — the same keys this form
    // uses — so a rule only the server can check lands under the box it is about.
    if (error?.fields) fieldErrors.value = { ...error.fields };
    useAlert(t('WASLO.MENUS.EDITOR.SAVE_FAILED'));
  } finally {
    isSaving.value = false;
  }
};

const remove = async () => {
  if (isRemoving.value || menuId.value === null) return;
  isRemoving.value = true;
  try {
    await wasloApi.deleteMenu(accountId.value, menuId.value);
    useAlert(t('WASLO.MENUS.EDITOR.REMOVE.REMOVED'));
    if (listUrl.value) await router.replace(listUrl.value);
  } catch {
    confirmingRemoval.value = false;
    useAlert(t('WASLO.MENUS.EDITOR.REMOVE.FAILED'));
  } finally {
    isRemoving.value = false;
  }
};

const reload = () => {
  if (!isNew.value) loadMenu();
  tasks.reload();
};
</script>

<template>
  <WasloPageLayout
    :header-title="headerTitle"
    :back-url="listUrl || ''"
    :back-label="t('WASLO.MENUS.TITLE')"
    :is-fetching="isFetching"
    :error-message="errorMessage"
    @retry="reload"
  >
    <template #body>
      <div class="flex flex-col gap-6">
        <MenuForm
          :model-value="draft"
          :errors="fieldErrors"
          :tasks="taskChoices"
          :languages="languages"
          @update:model-value="onDraftChange"
        >
          <template v-if="!isNew" #danger>
            <span class="w-full h-px mt-2 bg-n-weak" />
            <div class="flex flex-col gap-6">
              <SettingsHeader
                :heading="t('WASLO.MENUS.EDITOR.REMOVE.HEADING')"
                :description="t('WASLO.MENUS.EDITOR.REMOVE.DESCRIPTION')"
              />
              <div
                v-if="confirmingRemoval"
                class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-ruby-8 bg-n-solid-1"
                data-waslo-menu-remove
              >
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ t('WASLO.MENUS.EDITOR.REMOVE.CONFIRM') }}
                </p>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ t('WASLO.MENUS.EDITOR.REMOVE.CONFIRM_BODY') }}
                </p>
                <div class="flex flex-wrap items-center gap-3">
                  <Button
                    :label="t('WASLO.MENUS.EDITOR.REMOVE.CONFIRM_ACTION')"
                    variant="solid"
                    color="ruby"
                    size="sm"
                    :is-loading="isRemoving"
                    data-waslo-menu-remove-confirm
                    @click="remove"
                  />
                  <Button
                    :label="t('WASLO.MENUS.EDITOR.REMOVE.CANCEL')"
                    variant="faded"
                    color="slate"
                    size="sm"
                    @click="confirmingRemoval = false"
                  />
                </div>
              </div>
              <Button
                v-else
                :label="t('WASLO.MENUS.EDITOR.REMOVE.ACTION')"
                icon="i-lucide-trash-2"
                variant="faded"
                color="ruby"
                size="sm"
                class="self-start"
                data-waslo-menu-remove-start
                @click="confirmingRemoval = true"
              />
            </div>
          </template>
        </MenuForm>
        <!-- P-6.11: this menu's saves. Hidden until there is one to go back to. -->
        <RestoreHistory
          v-if="!isNew"
          ref="historyRef"
          :account-id="accountId"
          kind="menu"
          :item-id="menuId"
          @restored="loadMenu"
        />
      </div>
    </template>

    <template #footer>
      <WasloSaveBar
        :state="saveState"
        :status="saveStatus"
        :detail="saveDetail"
        :save-label="saveLabel"
        :disabled="isSaving"
        @save="save"
      />
    </template>
  </WasloPageLayout>
</template>
